import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBousanmSolide } from '../context/BousanmSolideContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Calendar,
  Plus,
  Users,
  Target,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  TrendingUp,
  Clock,
  Percent
} from 'lucide-react';

const DashboardBS = () => {
  const { globalStats, loading, fetchGlobalStats } = useBousanmSolide();
  const { t } = useTranslation(['solide', 'common']);

  useEffect(() => {
    fetchGlobalStats();
  }, [fetchGlobalStats]);

  const stats = globalStats?.statistiques || globalStats;

  if (loading) {
    return (
      <div className="h-full w-full bg-gray-50/40 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-gray-50/40 overflow-y-auto">
      {/* Header Row */}
      <div className="flex items-center justify-between mb-4 px-3 pt-3 gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-9 gap-2 text-sm hidden md:flex">
            <Calendar className="h-4 w-4" />
            <span className="hidden lg:inline">23 Oct 2025 - 19 Nov 2025</span>
            <span className="lg:hidden">Oct - Nov 2025</span>
          </Button>
        </div>
        <Link to="/bousanm-solide/clients" style={{ textDecoration: 'none' }}>
          <Button className="h-9 gap-2 bg-black hover:bg-gray-800 text-white text-sm">
            <span className="hidden sm:inline">{t('solide:buttons.clients')}</span>
            <Plus className="h-4 w-4" />
          </Button>
        </Link>
      </div>

      {/* Main Grid */}
      <div className="grid gap-3 px-3 pb-3">
        {/* Top Row - 4 Cards */}
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {/* Taux Complétion */}
          <Card>
            <CardHeader className="pb-1">
              <CardTitle className="text-[11px] font-medium text-gray-600">
                {t('solide:dashboard.completionRate')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center justify-center">
                {/* Circle Progress */}
                <div className="relative w-16 h-16 mb-1.5">
                  <svg className="w-16 h-16 -rotate-90">
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="#e5e7eb"
                      strokeWidth="5"
                      fill="none"
                    />
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="#3b82f6"
                      strokeWidth="5"
                      fill="none"
                      strokeDasharray={`${Math.PI * 56}`}
                      strokeDashoffset={`${Math.PI * 56 * (1 - (stats?.taux_completion_moyen || 0) / 100)}`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-base font-bold">{stats?.taux_completion_moyen?.toFixed(1) || 0}%</span>
                  </div>
                </div>
                <p className="text-[9px] text-center text-gray-500 leading-tight px-1">
                  {t('solide:dashboard.completionRate')} <span className="font-semibold text-orange-500">{stats?.taux_completion_moyen?.toFixed(1) || 0}%</span>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Total Clients BS */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-0.5">
              <CardTitle className="text-[11px] font-medium text-gray-600">
                {t('solide:dashboard.totalClients')}
              </CardTitle>
              <div className="h-6 w-6 rounded-md bg-blue-50 flex items-center justify-center">
                <Users className="h-3 w-3 text-blue-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold text-gray-900">
                {stats?.total_clients_bs?.toLocaleString() || '0'}
              </div>
              <div className="flex items-center text-[11px] text-gray-500 mt-0.5">
                <span className="text-green-600 font-medium">
                  {stats?.total_comptes_actifs || 0} {t('solide:dashboard.active')}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Montant Total Épargne */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-0.5">
              <CardTitle className="text-[11px] font-medium text-gray-600">
                {t('solide:dashboard.totalSavings')}
              </CardTitle>
              <div className="h-6 w-6 rounded-md bg-purple-50 flex items-center justify-center">
                <Wallet className="h-3 w-3 text-purple-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-0.5 mb-1">
                <div className="text-base font-bold text-blue-700">
                  {stats?.montant_total_epargne_htg?.toLocaleString('fr-HT', { style: 'currency', currency: 'HTG' }) || '0 G'}
                </div>
                <div className="text-sm font-bold text-emerald-600">
                  {stats?.montant_total_epargne_usd?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) || '$0.00'}
                </div>
              </div>
              <div className="flex flex-col text-[10px] text-gray-500 mt-1 border-t pt-1 border-dashed">
                <span className="text-green-600 font-medium flex justify-between">
                  <span>{t('solide:dashboard.bonus')}:</span> <span>+{stats?.bonis_total_accumule_htg?.toLocaleString() || 0} G</span>
                </span>
                <span className="text-emerald-600 font-medium flex justify-between">
                  <span>{t('solide:dashboard.bonus')}:</span> <span>+{stats?.bonis_total_accumule_usd?.toLocaleString() || 0} $</span>
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Commission Totale Agents */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-0.5">
              <CardTitle className="text-[11px] font-medium text-gray-600">
                {t('solide:dashboard.agentCommission')}
              </CardTitle>
              <div className="h-6 w-6 rounded-md bg-green-50 flex items-center justify-center">
                <DollarSign className="h-3 w-3 text-green-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-0.5 mb-1">
                <div className="text-base font-bold text-blue-700">
                  {stats?.commission_totale_agents_htg?.toLocaleString('fr-HT', { minimumFractionDigits: 2 }) || '0.00'} G
                </div>
                <div className="text-sm font-bold text-emerald-600">
                  {stats?.commission_totale_agents_usd?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) || '$0.00'}
                </div>
              </div>
              <div className="flex items-center text-[11px] text-gray-500 mt-1 pt-1 border-t border-dashed">
                <span className="text-blue-600 font-medium">
                  {stats?.total_agents_avec_clients || 0} {t('solide:dashboard.activeAgents')}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row - 3 Cards */}
        <div className="grid gap-3 grid-cols-1 lg:grid-cols-3">
          {/* Ponctualité */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-1">
              <CardTitle className="text-xs font-semibold">{t('solide:dashboard.globalPunctuality')}</CardTitle>
              <div className="h-7 w-7 rounded-md bg-green-50 flex items-center justify-center">
                <Clock className="h-4 w-4 text-green-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900 mb-3">
                {stats?.taux_ponctualite_global?.toFixed(1) || 0}%
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-[11px]">
                  <span className="text-gray-600">{t('solide:dashboard.onTime')}</span>
                  <span className="font-semibold text-green-600">
                    {stats?.semaines_a_lheure_total || 0} {t('solide:dashboard.weeks')}
                  </span>
                </div>
                <div className="flex justify-between text-[11px]">
                  <span className="text-gray-600">{t('solide:dashboard.late')}</span>
                  <span className="font-semibold text-orange-600">
                    {stats?.semaines_en_retard_total || 0} {t('solide:dashboard.weeks')}
                  </span>
                </div>
                <div className="flex justify-between text-[11px]">
                  <span className="text-gray-600">{t('solide:dashboard.tooLate')}</span>
                  <span className="font-semibold text-red-600">
                    {stats?.semaines_trop_tard_total || 0} {t('solide:dashboard.weeks')}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Montant Hebdo Moyen */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-1">
              <CardTitle className="text-xs font-semibold">{t('solide:dashboard.averageWeekly')}</CardTitle>
              <div className="h-7 w-7 rounded-md bg-blue-50 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-blue-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-1 mb-2">
                <div className="text-xl font-bold text-blue-700">
                  {stats?.montant_hebdo_moyen_htg?.toLocaleString() || 0} G
                </div>
                <div className="text-lg font-bold text-emerald-600">
                  {stats?.montant_hebdo_moyen_usd?.toLocaleString() || 0} $
                </div>
              </div>
              <p className="text-[11px] text-gray-500 mb-3">
                {t('solide:dashboard.weeklyAvgDesc')}
              </p>
              <div className="pt-3 border-t border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="text-[11px] text-gray-600">{t('solide:dashboard.totalSaved')}</span>
                  <div className="flex flex-col items-end text-[10px]">
                    <span className="font-semibold text-blue-600">{stats?.montant_total_epargne_htg?.toLocaleString() || 0} G</span>
                    <span className="font-semibold text-emerald-600">{stats?.montant_total_epargne_usd?.toLocaleString() || 0} $</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bonis Accumulés */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-1">
              <CardTitle className="text-xs font-semibold">{t('solide:dashboard.accumulatedBonus')}</CardTitle>
              <div className="h-7 w-7 rounded-md bg-purple-50 flex items-center justify-center">
                <Percent className="h-4 w-4 text-purple-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-1 mb-2">
                <div className="text-xl font-bold text-blue-700">
                  {stats?.bonis_total_accumule_htg?.toLocaleString() || 0} G
                </div>
                <div className="text-lg font-bold text-emerald-600">
                  {stats?.bonis_total_accumule_usd?.toLocaleString() || 0} $
                </div>
              </div>
              <p className="text-[11px] text-gray-500 mb-3">
                {t('solide:dashboard.bonusOnSavings')}
              </p>
              <div className="pt-3 border-t border-gray-100 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-[11px] text-gray-600">{t('solide:dashboard.bonusRate')} (HTG)</span>
                  <span className="text-[11px] font-semibold text-purple-600">
                    {stats?.montant_total_epargne_htg
                      ? ((stats.bonis_total_accumule_htg / stats.montant_total_epargne_htg) * 100).toFixed(2)
                      : 0}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[11px] text-gray-600">{t('solide:dashboard.savingsBase')}</span>
                  <div className="flex flex-col items-end text-[10px]">
                    <span className="font-semibold text-blue-600">{stats?.montant_total_epargne_htg?.toLocaleString() || 0} G</span>
                    <span className="font-semibold text-emerald-600">{stats?.montant_total_epargne_usd?.toLocaleString() || 0} $</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardBS;
